import * as React from "react";

import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  useQuickSearchHotkey,
  useResourceView,
  type Filter,
  type FilterMatchMode,
  type QuickSearchProperty,
} from "@/shared/components/ResourceView";
import type {
  MetricDefinitionRecord,
  UpdateMetricDefinitionRequest,
} from "@/shared/domain/metrics";
import {
  useAllMetricDefinitions,
  useUpdateMetricDefinition,
} from "@/shared/domain/metrics/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import { Banner } from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  createColumnHelper,
  useInMemoryRows,
  type ColDef,
  type ColumnPickerOption,
} from "@/shared/foundation/primitives/Table";

import { DescriptionCell, NameCell, UnitCell } from "./definitionCells";
import { definitionFilterFields, filterDefinitions } from "./definitionFilters";
import {
  DefinitionsTableContext,
  type DefinitionEdit,
  type DefinitionsTableActions,
} from "./definitionsTableContext";
import styles from "./MetricDefinitionsTable.module.css";

export interface MetricDefinitionsTableProps {
  orgId: string | undefined;
}

const columnHelper = createColumnHelper<MetricDefinitionRecord>();

/** The table's page size: large enough that most orgs fit on one page. */
const ROWS_PER_PAGE = 100;

/** Tall enough for the 36px unit field with 6px above and below it. */
const ROW_HEIGHT = 48;

const NO_FILTERS: readonly Filter[] = [];
const NO_DEFINITIONS: readonly MetricDefinitionRecord[] = [];

// A metric name has no ID shape to route on, so the search looks only in
// names, as on the session's Metrics tab.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "name", name: "Name", fallback: true },
];

/** The columns shown until the user picks others; Name is always shown. */
const DEFAULT_VISIBLE_COLUMN_IDS = [
  "description",
  "unit",
  "created",
  "modified",
];

const SORT_KEYS = {
  name: (definition: MetricDefinitionRecord) => definition.name,
  description: (definition: MetricDefinitionRecord) => definition.description,
  unit: (definition: MetricDefinitionRecord) => definition.unit,
  created: (definition: MetricDefinitionRecord) => definition.created,
  modified: (definition: MetricDefinitionRecord) => definition.modified,
};

/** Keys typed into a cell's field are the field's, not the grid's. */
function isTypingInField({ event }: { event: KeyboardEvent }): boolean {
  return (
    event.target instanceof HTMLElement &&
    event.target.closest("input, textarea") !== null
  );
}

const COLUMNS: ColDef<MetricDefinitionRecord>[] = [
  {
    colId: "name",
    headerName: "Name",
    flex: 2,
    minWidth: 220,
    hide: false,
    lockVisible: true,
    sortable: true,
    cellRenderer: NameCell,
  },
  {
    colId: "description",
    headerName: "Description",
    flex: 3,
    minWidth: 200,
    sortable: true,
    cellClass: styles.editableCell,
    cellRenderer: DescriptionCell,
    suppressKeyboardEvent: isTypingInField,
  },
  {
    colId: "unit",
    headerName: "Unit",
    width: 140,
    sortable: true,
    cellClass: styles.editableCell,
    cellRenderer: UnitCell,
    suppressKeyboardEvent: isTypingInField,
  },
  columnHelper.timestamp("created", {
    headerName: "Created",
    width: 160,
    sortable: true,
    variant: "short",
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 160,
    sortable: true,
    variant: "short",
  }),
];

/** The columns the picker offers; Name is always shown. */
const COLUMN_PICKER_OPTIONS: ColumnPickerOption[] = COLUMNS.filter(
  (column) => !column.lockVisible,
).map((column) => ({
  id: column.colId ?? String(column.field),
  label: column.headerName ?? "",
}));

/**
 * Management table for an org's metric definitions. Find definitions, or
 * Cmd/Ctrl-K, opens the quick search the other list pages use, which adds a
 * Name filter; the Filter menu also searches descriptions. Search, the Filter
 * and sorting all work on the full list, which loads at once. A description
 * or unit is edited in place by clicking its cell.
 */
export function MetricDefinitionsTable({ orgId }: MetricDefinitionsTableProps) {
  const query = useAllMetricDefinitions(orgId);
  const definitions = query.data ?? NO_DEFINITIONS;

  const view = useResourceView<MetricDefinitionRecord>({
    getRowId: (row) => row.metric_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
    preferences: { rowsPerPage: ROWS_PER_PAGE },
    initialSort: { columnId: "name", direction: "asc" },
  });
  const filters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";
  const quickSearch = useQuickSearchHotkey();

  const filterFields = React.useMemo(
    () => definitionFilterFields(definitions),
    [definitions],
  );
  const unitCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const definition of definitions) {
      if (definition.unit) {
        counts.set(definition.unit, (counts.get(definition.unit) ?? 0) + 1);
      }
    }
    return counts;
  }, [definitions]);

  // TODO(ENG-2582): paginate. `useAllMetricDefinitions` drains every page,
  // so search, the Filter and sorting run here, on the full list.
  const matching = React.useMemo(
    () => filterDefinitions(definitions, { filters, matchMode }),
    [definitions, filters, matchMode],
  );
  const { visibleRows, totalRowCount, hasNextPage } = useInMemoryRows(
    view.table,
    matching,
    SORT_KEYS,
  );

  // -- Editing ------------------------------------------------------------------

  const [editing, setEditing] = React.useState<DefinitionEdit | null>(null);
  // The open edit as of the latest start or stop, for a save that finishes
  // after the cells last rendered. Every change goes through the two
  // functions below, which keep it and `editing` in step.
  const openEdit = React.useRef<DefinitionEdit | null>(null);
  const startEditing = React.useCallback((edit: DefinitionEdit) => {
    openEdit.current = edit;
    setEditing(edit);
  }, []);
  const stopEditing = React.useCallback((edit: DefinitionEdit) => {
    if (openEdit.current === edit) {
      openEdit.current = null;
      setEditing(null);
    }
  }, []);
  const isEditing = React.useCallback(
    (edit: DefinitionEdit) => openEdit.current === edit,
    [],
  );
  const { mutateAsync: update } = useUpdateMetricDefinition();

  const actions = React.useMemo<DefinitionsTableActions>(
    () => ({
      editing,
      startEditing,
      stopEditing,
      isEditing,
      // Each edit sends only the field it changed, so it never overwrites the
      // other with a stale copy.
      save: (
        definition: MetricDefinitionRecord,
        patch: UpdateMetricDefinitionRequest,
      ) => update({ name: definition.name, request: patch, orgId }),
      unitCounts,
    }),
    [editing, startEditing, stopEditing, isEditing, update, orgId, unitCounts],
  );

  // -- Render -------------------------------------------------------------------

  if (query.error) {
    return (
      <Banner
        variant="error"
        text={`Failed to load definitions: ${query.error.message}`}
      />
    );
  }

  return (
    <div className={styles.tableSlot}>
      <ResourceView.Root>
        <QuickSearch
          open={quickSearch.isOpen}
          onClose={quickSearch.close}
          properties={QUICK_SEARCH_PROPERTIES}
          placeholder="Search by name…"
          onSubmit={(filter) => {
            view.clearFilters();
            view.commit({ type: "structured", filter });
          }}
        />
        <ResourceView.Actions>
          <QuickSearchButton
            onClick={quickSearch.open}
            label="Find definitions"
          />
          <ResourceView.Filters
            filterDefinitions={filterFields}
            filters={view.filters}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={COLUMN_PICKER_OPTIONS}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterFields}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DefinitionsTableContext.Provider value={actions}>
          <DataTable<MetricDefinitionRecord>
            table={view.table}
            ariaLabel="Metric definitions"
            columns={COLUMNS}
            data={visibleRows}
            totalRowCount={totalRowCount}
            hasNextPage={hasNextPage}
            // Skeleton rows only while the list first loads: a background
            // refetch keeps the rows, and any editor open in one, on screen.
            loading={query.isLoading ? "rowsOnly" : "none"}
            rowHeight={ROW_HEIGHT}
            noRowsContent={
              // Empty while there are definitions means the search and the
              // filters leave none.
              <ResourceView.EmptyState
                hasActiveFilters={definitions.length > 0}
                noData={{
                  icon: ENTITY_ICONS.metric,
                  title: "No metric definitions yet",
                  description:
                    "A definition appears the first time an action or the SDK publishes a metric. Add its description and unit here.",
                }}
                noMatches={{
                  icon: ENTITY_ICONS.metric,
                  title: "No matching metric definitions",
                  description:
                    "Try adjusting or clearing the search and filters.",
                }}
              />
            }
          />
        </DefinitionsTableContext.Provider>
      </ResourceView.Root>
    </div>
  );
}
